import {
  getCollaborationServer,
  getCollaborationSocket,
} from './CollaborationServer';
import TokenManager, { type TokenPayload } from './TokenManager';
import { createFayeClient } from 'javascripts/faye_client';
import {
  type CollaborationConfiguration,
  type FetchUserInfo,
} from '@aha-app/aha-editor-v2';
import setupFetchUserInfo from './fetchUserInfo';

// Editor tokens must be refreshed every 4 minutes. This is the maximum time
// a user can keep editing after their permissions have changed or been revoked.
// Note that even before the token expires, the server will reject any changes
// that the user is not allowed to make.
const DEFAULT_TOKEN_LIFETIME = 4 * 60 * 1000;

type CollaborationInitializationOptions = {
  // Numeric version of the current document content.
  documentVersion: number;
  // Typically the numeric ID of the record being edited.
  documentId: string;
  // Typically `${accountId}--${singularRecordType}`.
  documentCollection: string;
  // Base URL for persisting the record.
  baseURL: string;
  // Rails model name for the record being edited.
  objectType: string;

  // Optional parameters.

  // Callback for when the token changes to allow the parent component to update
  // if the token permissions change.
  tokenDidChange?: (token: TokenPayload) => void;
  // ID of the user currently editing the document.
  userId?: RecordId;
  // Handler that will be called on collaboration errors.
  onError?: (error: { code: number }, onRecovered: () => void) => boolean;
  // URL of the collaboration (sharedb) server. Will be read from the `window`
  // object if not provided.
  collaborationServerURL?: string;
  // Base URL for collaboration (to handle shared documents). Will be set to
  // the current origin if not provided.
  collaborationBaseURL?: string;
  // Optional function to get a sharing token for the current user.
  getSharingToken?: () => string;
  // URL for the Faye server that handles collaborative selections. If it is
  // not provided then the default will be used from the `window` object.
  selectionURL?: string;
  // Token for the collaborative selection server. If one is not provided then
  // it will be read from the `collaborative-selection-token` meta tag in the <head>.
  selectionToken?: string;
  // Optionally override the default token lifetime.
  tokenLifetime?: number;
};

interface CollaborationServerResult {
  tokenManager: TokenManager;
  collaborationOptions: CollaborationConfiguration;
  fetchUserInfo: FetchUserInfo;
}

// Initialize the collaboration options for a V2 editor.
function initializeV2Collaboration(
  options: CollaborationInitializationOptions
): CollaborationServerResult {
  const collaborationServer = getCollaborationServer(
    {
      baseURL: options.collaborationBaseURL,
      collaborationURL: options.collaborationServerURL,
    },
    2
  );

  const componentInstanceId = Math.random().toString(16).slice(2, 10);
  const clientDocumentId = [
    options.documentId,
    collaborationServer.clientId,
    componentInstanceId,
  ].join('--');

  const tokenManager = new TokenManager(
    collaborationServer,
    options.baseURL,
    options.documentCollection,
    clientDocumentId,
    options.tokenLifetime || DEFAULT_TOKEN_LIFETIME,
    options.tokenDidChange || (() => {}),
    options.getSharingToken
  );

  const currentUserId = window.currentUser?.id || '0';
  const currentUserName = window.currentUser?.name || 'User';

  const collaborationOptions: CollaborationConfiguration = {
    enabled: true,
    documentCollection: options.documentCollection,
    documentId: clientDocumentId,
    documentVersion: options.documentVersion,
    userInfo: {
      userId: options.userId || currentUserId,
      displayName: currentUserName,
    },
    connectionHandler: (onConnect: () => void) =>
      tokenManager.connect(onConnect),
    getCollaborationServer: () => getCollaborationSocket(collaborationServer),
    getFayeClient: () => {
      const fayeOptions = options.selectionURL
        ? {
            url: options.selectionURL,
            tokens: {
              collaborativeSelection: options.selectionToken,
            },
          }
        : undefined;
      // @ts-expect-error createFayeClient not typed
      return createFayeClient(fayeOptions);
    },
    onError: options.onError,
    getSharingToken: options.getSharingToken || (() => ''),
    baseURL: options.baseURL,
  };

  const fetchUserInfo = setupFetchUserInfo({
    // @ts-expect-error fetchUserInfo is not typed
    objectType: options.objectType,
    // @ts-expect-error fetchUserInfo is not typed
    documentId: options.documentId,
    baseURL: options.collaborationBaseURL,
    // @ts-expect-error fetchUserInfo is not typed
    getAuthorization: options.getSharingToken,
  });

  return { tokenManager, collaborationOptions, fetchUserInfo };
}

export default initializeV2Collaboration;
export { CollaborationServerResult };
